import { SEARCH_KIND_LABELS, type SearchResult, SearchResults } from '@deizi/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { useDebounced } from '../lib/use-debounced';

/** `GET /search` for the typed text (debounced); keeps the last list while typing. */
export function useGlobalSearch(q: string) {
  const term = useDebounced(q.trim(), 200);
  const query = useQuery({
    queryKey: ['search', term],
    queryFn: () => api.get(`/search?q=${encodeURIComponent(term)}&limit=8`, SearchResults),
    enabled: term.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
  return {
    term,
    results: term ? (query.data?.data ?? []) : [],
    loading: term !== q.trim() || query.isFetching,
    error: query.isError,
  };
}

export const SEARCH_EMPTY_HINT = 'Tente SKU, nº do pedido ou código do carretel (C-0009).';

/** One row per result: kind badge, label and the muted line (design/Topbar.dc.html). */
export function SearchRows({
  results,
  active,
  onPick,
  badgeWidth = 76,
}: {
  results: SearchResult[];
  active?: number;
  onPick: () => void;
  badgeWidth?: number;
}) {
  return results.map((r, i) => (
    <Link
      key={`${r.kind}-${r.id}`}
      to={r.href}
      role="option"
      aria-selected={i === active}
      className={i === active ? 'listrow on' : 'listrow'}
      style={{ gap: 12, padding: '10px 12px', alignItems: 'center' }}
      onClick={onPick}
    >
      <span className="badge" style={{ minWidth: badgeWidth, justifyContent: 'center' }}>
        {SEARCH_KIND_LABELS[r.kind]}
      </span>
      <span className="grow" style={{ minWidth: 0 }}>
        <span className="trunc" style={{ display: 'block', fontWeight: 600 }}>
          {r.label}
        </span>
        <span className="xs muted trunc" style={{ display: 'block' }}>
          {r.sub}
        </span>
      </span>
    </Link>
  ));
}
